import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { useList, errMsg, fmtRM, dayName, DAYS, apiFileUrl } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { ReplacementTasksCard } from "@/pages/reschedule/ReplacementWidgets";
import UpcomingReplacementsCard from "@/components/dashboard/UpcomingReplacementsCard";
import { FormDialog } from "@/components/ResourcePage";
import { PayrollDetailDialog } from "@/pages/admin/PayrollPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Sun, GraduationCap, CalendarDays, RefreshCcw, ClipboardCheck, MessageSquareText,
  Eye, KeyRound, FileDown, Save, CheckCircle2, Circle, ArrowRight, Plus, X,
  Gauge, PauseCircle, LifeBuoy, Clock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const nowTime = () => new Date().toTimeString().slice(0, 5);
const today = () => new Date().toISOString().slice(0, 10);

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function TeacherUpgradeDialog({ open, onOpenChange, student, onDone }) {
  const { data: tiles } = useList(open ? "/public/courses" : null);
  const [type, setType] = useState("exam_passed");
  const [newCourseId, setNewCourseId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const allLevels = (tiles || []).flatMap((t) => (t.levels || []).map((lv) => ({
    id: lv.id, label: `${t.name} · ${lv.name}`,
  })));
  const submit = async () => {
    if (!newCourseId) { toast.error("Pick the target grade"); return; }
    if (type === "exam_passed" && !confirmed) { toast.error("Tick the exam-pass confirmation"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/students/${student.id}/upgrade-grade`, {
        new_course_id: newCourseId, upgrade_type: type,
        exam_pass_confirmation: type === "exam_passed" ? confirmed : false,
        notes: notes || null, deposit_topup: true,
      });
      const d = r.data || {};
      if (d.status === "manual_review_required") {
        toast.warning(`Manual review required — ${d.manual_review_reason || "see admin"}`, { duration: 10000 });
      } else {
        toast.success(`Upgrade applied · ${d.status} · ${d.invoice_action || ""}`);
      }
      setNewCourseId(""); setNotes(""); setConfirmed(false);
      onDone && onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="teacher-upgrade-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-secondary" />Upgrade {student?.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            {[{ v: "exam_passed", label: "Exam passed (immediate)" }, { v: "non_exam", label: "Progression (notice rule)" }].map((o) => (
              <button key={o.v} type="button" onClick={() => setType(o.v)}
                className={`flex-1 rounded-md border px-3 py-2 text-xs ${type === o.v ? "border-secondary bg-secondary/10 text-secondary font-semibold" : "border-border"}`}
                data-testid={`tu-type-${o.v}`}>{o.label}</button>
            ))}
          </div>
          {type === "non_exam" && (
            <div className="rounded-md border bg-amber-50 p-3 text-xs">
              <b>Notice rule:</b> day 1-24 → 7-day notice (effective on 1st of next month). Day 25+ → 30-day notice. New fee starts on effective date.
            </div>
          )}
          <div><Label className="text-xs">Target grade *</Label>
            <Select value={newCourseId} onValueChange={setNewCourseId}>
              <SelectTrigger data-testid="tu-target"><SelectValue placeholder="Pick the new level…" /></SelectTrigger>
              <SelectContent className="max-h-[260px]">
                {allLevels.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type === "exam_passed" && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-secondary"
                checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="tu-confirm" />
              <span className="text-xs">I confirm {student?.full_name} passed the exam and the upgrade should take effect immediately.</span>
            </label>
          )}
          <div><Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="tu-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-secondary hover:bg-[#D96C52]" data-testid="tu-submit">
            {busy ? "Submitting…" : "Confirm upgrade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- shared dialogs ----------
function useAttendanceDialog(reloadFns = []) {
  const [target, setTarget] = useState(null); // schedule
  const fields = [
    { name: "lesson_date", label: "Lesson date", type: "date", required: true, defaultValue: today() },
    { name: "student_status", label: "Student status", type: "select", required: true, defaultValue: "present", options: [
      { value: "present", label: "Present" }, { value: "absent", label: "Absent" },
      { value: "late", label: "Late" }, { value: "excused", label: "Excused" }] },
    { name: "teacher_check_in_time", label: "My check-in time", type: "time", defaultValue: nowTime() },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
  const dialog = (
    <FormDialog
      open={!!target}
      onOpenChange={(o) => !o && setTarget(null)}
      title={`Attendance — ${target?.student_name || ""}`}
      fields={fields}
      initial={{ lesson_date: today(), student_status: "present", teacher_check_in_time: nowTime(), __key: target?.id }}
      submitLabel="Save attendance"
      onSubmit={async (values) => {
        await api.post("/attendance", { ...values, class_schedule_id: target.id });
        toast.success("Attendance saved");
        reloadFns.forEach((f) => f && f());
      }}
    />
  );
  return { openAttendance: setTarget, attendanceDialog: dialog };
}

function useRemarkDialog(reloadFns = []) {
  const [target, setTarget] = useState(null);
  const fields = [
    { name: "lesson_date", label: "Lesson date", type: "date", required: true },
    { name: "topic_covered", label: "Topic covered", type: "textarea", required: true },
    { name: "homework", label: "Homework", type: "textarea" },
    { name: "teacher_comment", label: "Comment for parents", type: "textarea" },
    { name: "media_url", label: "Media / video link", placeholder: "https://..." },
  ];
  const dialog = (
    <FormDialog
      open={!!target}
      onOpenChange={(o) => !o && setTarget(null)}
      title={`Lesson remark — ${target?.student_name || ""}`}
      fields={fields}
      initial={{ lesson_date: today(), __key: target?.id }}
      submitLabel="Save remark"
      onSubmit={async (values) => {
        await api.post("/lesson-remarks", { ...values, class_schedule_id: target.id });
        toast.success("Lesson remark saved — parents can see it now");
        reloadFns.forEach((f) => f && f());
      }}
    />
  );
  return { openRemark: setTarget, remarkDialog: dialog };
}

function TodayClassList({ overview, openAttendance, openRemark }) {
  const attended = new Set((overview.attendance_today || []).map((a) => a.class_schedule_id));
  const all = [...overview.todays_classes, ...(overview.todays_bookings || [])];
  if (all.length === 0) return <EmptyState text="No classes today. Enjoy the quiet!" />;
  return (
    <div className="space-y-3 stagger-in" data-testid="today-class-list">
      {all.map((c) => (
        <div key={c.id} className="rounded-lg border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 lift">
          <span className="font-mono text-sm w-28 shrink-0">{c.start_time}–{c.end_time}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{c.student_name} {c.course_name && `· ${c.course_name}`}</p>
            <p className="text-xs text-muted-foreground">{c.studio_name} {c.booking_type ? `· ${c.booking_type}` : ""}</p>
          </div>
          {c.day_of_week !== undefined && (
            <div className="flex gap-2 shrink-0">
              {attended.has(c.id) ? (
                <StatusBadge status="present" testId={`attended-badge-${c.id}`} />
              ) : (
                <Button size="sm" onClick={() => openAttendance(c)} data-testid={`mark-attendance-${c.id}`}>
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1" />Check in & attendance
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openRemark(c)} data-testid={`add-remark-${c.id}`}>
                <MessageSquareText className="h-3.5 w-3.5 mr-1" />Remark
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- pages ----------
function TeacherOnboardingBanner() {
  const { data: status, loading } = useList("/teacher/onboarding-status");
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== "undefined" && window.localStorage.getItem("teacher_onboarding_dismissed") === "1");
  if (loading || !status || status.complete || dismissed) return null;
  const items = [
    { key: "has_profile", label: "Complete your profile (bio + phone)",
      to: "/teacher/profile", done: status.has_profile },
    { key: "has_courses", label: "Pick the courses & grades you teach",
      to: "/teacher/profile", done: status.has_courses },
    { key: "has_availability", label: "Set your weekly available time slots",
      to: "/teacher/availability", done: status.has_availability },
  ];
  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/70 p-5"
         data-testid="teacher-onboarding-banner">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold text-amber-900 text-sm">Welcome — let&apos;s set up your account</p>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Finish these 3 steps so parents can book you and we can route students + notifications correctly.
          </p>
        </div>
        <button onClick={() => { window.localStorage.setItem("teacher_onboarding_dismissed", "1"); setDismissed(true); }}
                className="text-amber-700 hover:text-amber-900" data-testid="dismiss-onboarding"
                aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((it) => (
          <Link key={it.key} to={it.to}
                className="flex items-center gap-3 rounded-md bg-white/60 border border-amber-100 px-3 py-2 hover:bg-white transition"
                data-testid={`onboarding-step-${it.key}`}>
            {it.done
              ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              : <Circle className="h-4 w-4 text-amber-500 shrink-0" />}
            <span className={`text-sm flex-1 ${it.done ? "text-emerald-900 line-through opacity-70" : "text-amber-900"}`}>{it.label}</span>
            <ArrowRight className="h-3.5 w-3.5 text-amber-600" />
          </Link>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <Link to="/teacher/teaching-requests" className="text-xs text-amber-800 hover:text-amber-950 inline-flex items-center gap-1"
              data-testid="onboarding-request-course">
          <Plus className="h-3 w-3" />Request a new course to teach
        </Link>
      </div>
    </div>
  );
}

// ---------- Part C: Utilisation card + "Offer more teaching time" dialog ----------
const UTIL_STYLES = {
  healthy: { label: "Healthy", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", ring: "ring-emerald-300" },
  almost_full: { label: "Almost Full", cls: "bg-amber-100 text-amber-800 border-amber-200", ring: "ring-amber-300" },
  nearly_full: { label: "Nearly Full", cls: "bg-orange-100 text-orange-800 border-orange-200", ring: "ring-orange-300" },
  fully_booked: { label: "Fully Booked", cls: "bg-rose-100 text-rose-800 border-rose-200", ring: "ring-rose-300" },
};

function OfferMoreTimeDialog({ open, onOpenChange, defaultPct }) {
  const [day, setDay] = useState(1);
  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("21:00");
  const [instrument, setInstrument] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState(defaultPct >= 75
    ? "I'm highly utilised and happy to take on more classes."
    : "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (start >= end) { toast.error("End time must be after start time"); return; }
    setBusy(true);
    try {
      await api.post("/teacher/availability-requests", {
        day_of_week: Number(day),
        start_time: start,
        end_time: end,
        request_type: "add_time",
        instrument: instrument || undefined,
        effective_from: effectiveFrom || undefined,
        note,
      });
      toast.success("Thank you for offering more teaching time. Admin will review and approve before it becomes available for student booking.");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offer-time-dialog">
        <DialogHeader>
          <DialogTitle>Offer more teaching time</DialogTitle>
          <DialogDescription>
            Suggest extra weekly availability. The school admin will review and approve before it goes live.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Day</Label>
            <select value={day} onChange={(e) => setDay(e.target.value)}
                    data-testid="offer-time-day"
                    className="w-full border rounded-md px-3 py-2 text-sm bg-card">
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Start</Label>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                   data-testid="offer-time-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End</Label>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                   data-testid="offer-time-end" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Instrument / course (optional)</Label>
            <Input value={instrument} onChange={(e) => setInstrument(e.target.value)}
                   placeholder="e.g. Piano" data-testid="offer-time-instrument" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Effective from (optional)</Label>
            <Input type="date" value={effectiveFrom}
                   onChange={(e) => setEffectiveFrom(e.target.value)}
                   data-testid="offer-time-effective-from" />
          </div>
        </div>
        <div className="space-y-1.5 mt-2">
          <Label className="text-xs">Note to admin (optional)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="offer-time-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="offer-time-submit">
            {busy ? "Sending..." : "Send to admin"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UtilisationCard({ util }) {
  const [openDialog, setOpenDialog] = useState(false);
  const style = UTIL_STYLES[util?.status] || UTIL_STYLES.healthy;
  const pct = util?.utilisation_pct ?? 0;
  // Determine bar color
  const barColor = pct >= 100 ? "bg-rose-500"
                : pct >= 90  ? "bg-orange-500"
                : pct >= 75  ? "bg-amber-500"
                : "bg-emerald-500";
  const showOfferBtn = pct >= 75;
  return (
    <div className="rounded-lg border bg-card p-5 lg:col-span-2" data-testid="utilisation-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-secondary" />
            <h3 className="text-base font-semibold">Capacity utilisation</h3>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.cls}`}
                  data-testid="utilisation-badge">
              {style.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Based on your weekly available hours vs. assigned classes.
          </p>
        </div>
        {showOfferBtn && (
          <Button size="sm" onClick={() => setOpenDialog(true)} data-testid="offer-more-time-btn">
            <Plus className="h-3.5 w-3.5 mr-1" />Offer more teaching time
          </Button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Available / week</p>
          <p className="text-2xl font-bold" data-testid="util-available-hours">
            {util?.weekly_available_hours ?? 0}<span className="text-xs font-normal text-muted-foreground"> h</span>
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Utilised</p>
          <p className="text-2xl font-bold" data-testid="util-utilised-hours">
            {util?.weekly_utilised_hours ?? 0}<span className="text-xs font-normal text-muted-foreground"> h</span>
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Utilisation</p>
          <p className="text-2xl font-bold" data-testid="util-pct">
            {pct}<span className="text-xs font-normal text-muted-foreground">%</span>
          </p>
        </div>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }}
             data-testid="util-bar" />
      </div>
      {util?.weekly_available_hours === 0 && (
        <p className="text-xs text-amber-700 mt-3">
          You haven&apos;t set any weekly availability yet —{" "}
          <Link to="/teacher/availability" className="underline">add availability</Link> so we can compute this.
        </p>
      )}
      <OfferMoreTimeDialog open={openDialog} onOpenChange={setOpenDialog} defaultPct={pct} />
    </div>
  );
}

// ---------- Part B: Weekly schedule grid (Today / This Week / Next Week) ----------
function WeeklyScheduleTabs({ today, thisWeek, nextWeek }) {
  const [tab, setTab] = useState("today");
  const list = tab === "today" ? today : tab === "this_week" ? thisWeek : nextWeek;

  // Group by day of week for week views
  const grouped = useMemo(() => {
    if (tab === "today") return null;
    const groups = new Map();
    list.forEach((s) => {
      const k = s.day_of_week ?? 99;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(s);
    });
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [tab, list]);

  return (
    <div className="rounded-lg border bg-card p-5" data-testid="weekly-schedule-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-secondary" />
          <h3 className="text-base font-semibold">Teaching schedule</h3>
        </div>
        <div className="inline-flex rounded-md border bg-muted/30 p-1 text-xs" role="tablist">
          {[
            { k: "today", label: `Today (${today.length})` },
            { k: "this_week", label: `This week (${thisWeek.length})` },
            { k: "next_week", label: `Next week (${nextWeek.length})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`schedule-tab-${t.k}`}
              className={`px-3 py-1.5 rounded transition-colors ${tab === t.k
                ? "bg-card text-foreground shadow-sm border" : "text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {list.length === 0 ? (
        <EmptyState text={tab === "today" ? "No classes today. Enjoy the quiet!" : "No classes scheduled."} />
      ) : tab === "today" ? (
        <div className="space-y-2" data-testid="schedule-today-list">
          {list.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-md border bg-card/50 px-3 py-2.5 text-sm"
                 data-testid={`schedule-row-${c.id}`}>
              <Clock className="h-3.5 w-3.5 text-secondary shrink-0" />
              <span className="font-mono text-xs w-24 shrink-0">{c.start_time}–{c.end_time}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{c.student_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {c.course_name} {c.studio_name ? `· ${c.studio_name}` : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid={`schedule-grid-${tab}`}>
          {grouped.map(([dow, classes]) => (
            <div key={dow}>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-secondary mb-2">
                {dow === 99 ? "Unscheduled" : DAYS[dow]}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {classes.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs"
                       data-testid={`schedule-row-${c.id}`}>
                    <span className="font-mono w-20 shrink-0">{c.start_time || "—"}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{c.student_name}</p>
                      <p className="text-muted-foreground truncate">
                        {c.course_name}{c.studio_name ? ` · ${c.studio_name}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Part B: Pending replacement classes ----------
function PendingReplacementsCard({ items }) {
  return (
    <div className="rounded-lg border bg-card p-5" data-testid="pending-replacements-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-secondary" />
          <h3 className="text-base font-semibold">Replacement classes — yet to teach</h3>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
              data-testid="pending-replacements-count">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No replacement classes pending — all caught up.</p>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 5).map((r) => (
            <div key={r.id} className="rounded-md border bg-amber-50/30 px-3 py-2 text-xs"
                 data-testid={`replacement-row-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{r.student_name} {r.course_name && `· ${r.course_name}`}</p>
                  <p className="text-muted-foreground mt-0.5">
                    Original: <span className="font-mono">{r.original_date}</span>
                    {r.replacement_date && (
                      <> → Replacement: <span className="font-mono text-amber-800 font-medium">{r.replacement_date} {r.start_time}–{r.end_time}</span></>
                    )}
                  </p>
                  {r.studio_name && <p className="text-muted-foreground">{r.studio_name}</p>}
                </div>
              </div>
            </div>
          ))}
          {items.length > 5 && (
            <Link to="/teacher/reschedule" className="text-xs text-secondary hover:underline inline-flex items-center gap-1"
                  data-testid="view-all-replacements">
              View all {items.length} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Part B: Classes being rescheduled (pending requests) ----------
function BeingRescheduledCard({ items }) {
  return (
    <div className="rounded-lg border bg-card p-5" data-testid="being-rescheduled-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <RefreshCcw className="h-4 w-4 text-secondary" />
          <h3 className="text-base font-semibold">Classes being rescheduled</h3>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200"
              data-testid="being-rescheduled-count">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No reschedule requests in discussion.</p>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 5).map((r) => (
            <Link key={r.id} to={`/teacher/reschedule/${r.id}`}
                  className="block rounded-md border bg-blue-50/30 px-3 py-2 text-xs hover:bg-blue-50/60 transition"
                  data-testid={`being-resched-row-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{r.student_name}</p>
                  <p className="text-muted-foreground mt-0.5">
                    From <span className="font-mono">{r.original_date} {r.original_start_time}</span>
                    {r.proposed_date && (
                      <> → proposed <span className="font-mono text-blue-800 font-medium">{r.proposed_date} {r.proposed_start_time || ""}</span></>
                    )}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {r.requested_by_role === "teacher" ? "You requested" : `Requested by ${r.requested_by_role}`}
                    {" · "}status: <span className="font-medium">{r.status}</span>
                  </p>
                </div>
                <ArrowRight className="h-3 w-3 text-blue-600 shrink-0 mt-1" />
              </div>
            </Link>
          ))}
          {items.length > 5 && (
            <Link to="/teacher/reschedule" className="text-xs text-secondary hover:underline inline-flex items-center gap-1">
              View all {items.length} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Part B: Temporarily-stopped classes ----------
function TempStopCard({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-5" data-testid="temp-stop-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <PauseCircle className="h-4 w-4 text-rose-600" />
          <h3 className="text-base font-semibold">Classes on temporary stop</h3>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200"
              data-testid="temp-stop-count">
          {items.length}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((c) => (
          <div key={c.id} className="rounded-md border bg-card px-3 py-2 text-xs"
               data-testid={`temp-stop-row-${c.id}`}>
            <p className="font-medium">{c.student_name} {c.course_name && `· ${c.course_name}`}</p>
            <p className="text-muted-foreground mt-0.5">
              {c.day_of_week !== null && c.day_of_week !== undefined ? `${DAYS[c.day_of_week]} ` : ""}
              {c.start_time && `${c.start_time}–${c.end_time} `}
              {c.studio_name && `· ${c.studio_name}`}
            </p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        These students have paused lessons. Contact the school admin once they&apos;re ready to resume.
      </p>
    </div>
  );
}

export function TeacherDashboard() {
  const { data: overview, loading, reload } = useList("/teacher/overview");
  const { data: summary, loading: sumLoading } = useList("/teacher/dashboard-summary");
  const { openAttendance, attendanceDialog } = useAttendanceDialog([reload]);
  const { openRemark, remarkDialog } = useRemarkDialog([reload]);
  if (loading || !overview) return <Spinner full />;
  return (
    <div data-testid="teacher-dashboard">
      <PageHeader title={`Hello, ${overview.teacher.full_name.split(" ")[0]}`}
        description="Your teaching overview for today." />
      <TeacherOnboardingBanner />
      {overview.on_leave_today && (
        <div className="rounded-lg border-2 border-destructive/50 bg-destructive/5 p-4 mb-6 flex items-start gap-3"
             data-testid="teacher-on-leave-banner">
          <div className="h-9 w-9 shrink-0 rounded-full bg-destructive/15 flex items-center justify-center text-destructive font-bold">
            !
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-destructive">
              You are on {overview.on_leave_today.category === "mc" ? "Medical Leave"
                : overview.on_leave_today.category === "emergency" ? "Emergency Leave" : "Leave"} today
              {overview.on_leave_today.start_time
                ? ` (${overview.on_leave_today.start_time}–${overview.on_leave_today.end_time || "end of day"})`
                : " — full day"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {overview.on_leave_today.affected_count} replacement request(s) filed · {" "}
              <span className="text-primary font-medium">{overview.on_leave_today.replaced_count} confirmed</span>
              {overview.on_leave_today.pending_count > 0
                && <> · <span className="text-amber-600 font-medium">{overview.on_leave_today.pending_count} still pending</span></>}
            </p>
          </div>
          <Link to="/teacher/reschedule" className="text-xs text-primary hover:underline shrink-0"
                data-testid="teacher-on-leave-banner-link">
            Manage replacements <ArrowRight className="h-3 w-3 inline" />
          </Link>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-in">
        <StatCard icon={Sun} label="Classes today" value={overview.todays_classes.length + (overview.todays_bookings || []).length} accent testId="stat-today" />
        <StatCard icon={GraduationCap} label="My students" value={overview.students_count} testId="stat-my-students" />
        <StatCard icon={CalendarDays} label="Weekly classes" value={overview.weekly_classes} testId="stat-weekly" />
        <StatCard icon={RefreshCcw} label="Pending reschedules" value={overview.pending_reschedules} testId="stat-pending" />
      </div>

      {/* Part C: Capacity utilisation */}
      {summary && !sumLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <UtilisationCard util={summary.utilisation} />
          <div className="rounded-lg border bg-card p-5" data-testid="quick-actions-card">
            <h3 className="text-base font-semibold mb-3">Quick actions</h3>
            <div className="space-y-2 text-sm">
              <Link to="/teacher/availability" className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 hover:bg-muted/40 transition"
                    data-testid="qa-availability">
                <span>Set my availability</span><ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/teacher/teaching-requests" className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 hover:bg-muted/40 transition"
                    data-testid="qa-teaching">
                <span>Request a new course</span><ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/teacher/reschedule" className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 hover:bg-muted/40 transition"
                    data-testid="qa-reschedule">
                <span>Reschedule requests</span><ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Part B: Weekly teaching schedule */}
      {summary && !sumLoading && (
        <div className="mt-6">
          <WeeklyScheduleTabs
            today={summary.todays_classes || []}
            thisWeek={summary.this_week_classes || []}
            nextWeek={summary.next_week_classes || []} />
        </div>
      )}

      {/* Part B: Replacements + reschedules */}
      {summary && !sumLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <PendingReplacementsCard items={summary.pending_replacements || []} />
          <BeingRescheduledCard items={summary.being_rescheduled || []} />
        </div>
      )}

      {/* Upcoming confirmed replacements (next 7/14/30 days) */}
      <div className="mt-4"><UpcomingReplacementsCard role="teacher" /></div>

      {/* Replacement tasks from approved leave + partial minutes owed */}
      <div className="mt-4"><ReplacementTasksCard /></div>

      {/* Part B: Temp-stop */}
      {summary && !sumLoading && (
        <div className="mt-4">
          <TempStopCard items={summary.temp_stop_classes || []} />
        </div>
      )}

      <h3 className="text-xl font-semibold mt-10 mb-4">Today&apos;s classes (check-in)</h3>
      <TodayClassList overview={overview} openAttendance={openAttendance} openRemark={openRemark} />
      {attendanceDialog}{remarkDialog}
    </div>
  );
}

export function TeacherToday() {
  const { data: overview, loading, reload } = useList("/teacher/overview");
  const { openAttendance, attendanceDialog } = useAttendanceDialog([reload]);
  const { openRemark, remarkDialog } = useRemarkDialog([reload]);
  if (loading || !overview) return <Spinner full />;
  return (
    <div data-testid="teacher-today-page">
      <PageHeader title="Today's classes" description="Check in, mark attendance and leave lesson remarks." />
      <TodayClassList overview={overview} openAttendance={openAttendance} openRemark={openRemark} />
      {attendanceDialog}{remarkDialog}
    </div>
  );
}

export function TeacherTimetable() {
  const { data: schedules, loading } = useList("/schedules");
  if (loading) return <Spinner full />;
  const byDay = DAYS.map((d, i) => ({ day: d, classes: (schedules || []).filter((s) => s.day_of_week === i && s.status === "active") }));
  return (
    <div data-testid="teacher-timetable-page">
      <PageHeader title="My timetable" description="Your weekly recurring classes." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in">
        {byDay.filter((d) => d.classes.length > 0).map((d) => (
          <div key={d.day} className="rounded-lg border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">{d.day}</p>
            {d.classes.map((c) => (
              <Link key={c.id} to={`/teacher/classes/${c.id}`} className="block py-2 border-b last:border-0 hover:bg-muted/50 rounded px-1 transition-colors" data-testid={`timetable-class-${c.id}`}>
                <p className="text-sm font-medium">{c.start_time}–{c.end_time} · {c.student_name}</p>
                <p className="text-xs text-muted-foreground">{c.course_name} · {c.studio_name}</p>
              </Link>
            ))}
          </div>
        ))}
        {byDay.every((d) => d.classes.length === 0) && <EmptyState text="No classes assigned yet." />}
      </div>
    </div>
  );
}

export function TeacherStudents() {
  const { data: students, loading, reload } = useList("/teacher/students");
  const [upgradeFor, setUpgradeFor] = useState(null);
  if (loading) return <Spinner full />;
  return (
    <div data-testid="teacher-students-page">
      <PageHeader title="My students" description="Only students assigned to your classes are listed here." />
      {!students || students.length === 0 ? <EmptyState text="No students yet." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in">
          {students.map((s) => (
            <div key={s.id} className="rounded-lg border bg-card p-5 lift" data-testid={`student-card-${s.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold">{s.full_name}</p>
                <StatusBadge status={s.status} />
              </div>
              <p className="text-xs text-muted-foreground mb-3">Level: {s.current_level || "—"}</p>
              {s.classes.map((c, i) => (
                <p key={i} className="text-xs py-1 border-b last:border-0">
                  {c.course_name} · {dayName(c.day_of_week)} {c.start_time}–{c.end_time}
                </p>
              ))}
              {s.status === "active" && (
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setUpgradeFor(s)} data-testid={`teacher-upgrade-${s.id}`}>
                  <GraduationCap className="h-4 w-4 mr-1.5" />Upgrade grade
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <TeacherUpgradeDialog open={!!upgradeFor} onOpenChange={(o) => !o && setUpgradeFor(null)}
                             student={upgradeFor} onDone={reload} />
    </div>
  );
}

export function TeacherClassDetail() {
  const { id } = useParams();
  const { data: c, loading, reload } = useList(`/teacher/classes/${id}`);
  const { openAttendance, attendanceDialog } = useAttendanceDialog([reload]);
  const { openRemark, remarkDialog } = useRemarkDialog([reload]);
  if (loading || !c) return <Spinner full />;
  return (
    <div data-testid="teacher-class-detail">
      <PageHeader title={`${c.student_name} — ${c.course_name}`}
        description={`${dayName(c.day_of_week)} ${c.start_time}–${c.end_time} · ${c.studio_name}`}
        actions={
          <>
            <Button size="sm" onClick={() => openAttendance(c)} data-testid="class-mark-attendance">
              <ClipboardCheck className="h-4 w-4 mr-1" />Attendance
            </Button>
            <Button size="sm" variant="outline" onClick={() => openRemark(c)} data-testid="class-add-remark">
              <MessageSquareText className="h-4 w-4 mr-1" />Remark
            </Button>
          </>
        } />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Attendance history</h3>
          {c.attendance.length === 0 ? <EmptyState text="No attendance yet." /> : c.attendance.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
              <span>{a.lesson_date}</span>
              <div className="flex gap-1.5">
                <StatusBadge status={a.student_status} />
                {a.punctuality_status && <StatusBadge status={a.punctuality_status} />}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Lesson remarks</h3>
          {c.remarks.length === 0 ? <EmptyState text="No remarks yet." /> : c.remarks.map((r) => (
            <div key={r.id} className="py-2.5 border-b last:border-0 text-sm">
              <p className="text-xs text-muted-foreground">{r.lesson_date}</p>
              <p className="font-medium">{r.topic_covered}</p>
              {r.homework && <p className="text-xs mt-0.5">Homework: {r.homework}</p>}
              {r.parent_comment && <p className="text-xs mt-1 text-secondary">Parent: {r.parent_comment}</p>}
            </div>
          ))}
        </div>
      </div>
      {attendanceDialog}{remarkDialog}
    </div>
  );
}

export function TeacherAttendance() {
  const { data: schedules } = useList("/schedules");
  const { data: attendance, loading, reload } = useList("/attendance");
  const { openAttendance, attendanceDialog } = useAttendanceDialog([reload]);
  const [pick, setPick] = useState("");
  return (
    <div data-testid="teacher-attendance-page">
      <PageHeader title="Attendance" description="Your attendance and punctuality records."
        actions={
          <div className="flex gap-2">
            <select data-testid="attendance-class-picker" className="border rounded-md px-3 py-2 text-sm bg-card"
              value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Mark for class...</option>
              {(schedules || []).map((s) => (
                <option key={s.id} value={s.id}>{s.student_name} · {dayName(s.day_of_week)} {s.start_time}</option>
              ))}
            </select>
            <Button disabled={!pick} onClick={() => openAttendance((schedules || []).find((s) => s.id === pick))} data-testid="open-attendance-button">
              Mark
            </Button>
          </div>
        } />
      {loading ? <Spinner full /> : !attendance || attendance.length === 0 ? <EmptyState text="No attendance records." /> : (
        <div className="rounded-lg border bg-card divide-y">
          {attendance.map((a) => (
            <div key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono text-xs w-24">{a.lesson_date}</span>
              <span className="font-medium flex-1">{a.student_name} · {a.course_name}</span>
              <StatusBadge status={a.student_status} />
              {a.punctuality_status && <StatusBadge status={a.punctuality_status} />}
            </div>
          ))}
        </div>
      )}
      {attendanceDialog}
    </div>
  );
}

export function TeacherRemarks() {
  const { data: schedules } = useList("/schedules");
  const { data: remarks, loading, reload } = useList("/lesson-remarks");
  const { openRemark, remarkDialog } = useRemarkDialog([reload]);
  const [pick, setPick] = useState("");
  return (
    <div data-testid="teacher-remarks-page">
      <PageHeader title="Lesson remarks" description="Notes, homework and media links visible to parents."
        actions={
          <div className="flex gap-2">
            <select data-testid="remark-class-picker" className="border rounded-md px-3 py-2 text-sm bg-card"
              value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Add for class...</option>
              {(schedules || []).map((s) => (
                <option key={s.id} value={s.id}>{s.student_name} · {dayName(s.day_of_week)} {s.start_time}</option>
              ))}
            </select>
            <Button disabled={!pick} onClick={() => openRemark((schedules || []).find((s) => s.id === pick))} data-testid="open-remark-button">
              Add
            </Button>
          </div>
        } />
      {loading ? <Spinner full /> : !remarks || remarks.length === 0 ? <EmptyState text="No remarks yet." /> : (
        <div className="space-y-3 stagger-in">
          {remarks.map((r) => (
            <div key={r.id} className="rounded-lg border bg-card p-5" data-testid={`remark-${r.id}`}>
              <p className="text-xs text-muted-foreground">{r.lesson_date} · {r.student_name} · {r.course_name}</p>
              <p className="font-medium text-sm mt-1">{r.topic_covered}</p>
              {r.homework && <p className="text-sm mt-1"><span className="text-muted-foreground">Homework:</span> {r.homework}</p>}
              {r.teacher_comment && <p className="text-sm mt-1"><span className="text-muted-foreground">Comment:</span> {r.teacher_comment}</p>}
              {r.media_url && <a className="text-sm text-secondary underline" href={r.media_url} target="_blank" rel="noreferrer">Lesson media</a>}
              {r.parent_comment && <p className="text-sm mt-2 border-l-2 border-secondary pl-3">Parent: {r.parent_comment}</p>}
            </div>
          ))}
        </div>
      )}
      {remarkDialog}
    </div>
  );
}

export function TeacherPayroll() {
  const { data: payrolls, loading } = useList("/payroll/me");
  const [detailId, setDetailId] = useState(null);
  return (
    <div data-testid="teacher-payroll-page">
      <PageHeader title="My payroll"
        description="Your commission is calculated on the music course fee only — SST and the school admin fee are excluded." />
      {loading ? <Spinner full /> : !payrolls || payrolls.length === 0 ? <EmptyState text="No payroll records yet." /> : (
        <div className="space-y-3 stagger-in">
          {payrolls.map((p) => (
            <div key={p.id} className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-4" data-testid={`my-payroll-${p.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold">{p.payroll_year}-{String(p.payroll_month).padStart(2, "0")}</p>
                <p className="text-xs text-muted-foreground">
                  Gross {fmtRM(p.gross_teacher_commission)} · Deductions {fmtRM(p.total_employee_deductions)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Net payable</p>
                <Money value={p.net_payable_to_teacher} className="font-bold text-base" />
              </div>
              <StatusBadge status={p.status} />
              <Button variant="ghost" size="sm" onClick={() => setDetailId(p.id)} data-testid={`view-my-payroll-${p.id}`}>
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => window.open(apiFileUrl(`/payroll/${p.id}/payslip-pdf`), "_blank")}
                data-testid={`download-my-payslip-${p.id}`}>
                <FileDown className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <PayrollDetailDialog payrollId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}

export function TeacherProfile() {
  const { data: profile, loading, reload } = useList("/teacher/profile");
  const [phone, setPhone] = useState(null);
  const [bio, setBio] = useState(null);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  if (loading || !profile) return <Spinner full />;

  const save = async () => {
    try {
      await api.put("/teacher/profile", { phone: phone ?? profile.phone, bio: bio ?? profile.bio });
      toast.success("Profile updated");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password changed");
      setCurPw(""); setNewPw("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="teacher-profile-page">
      <PageHeader title="My profile" description={profile.email} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h3 className="text-lg font-semibold">Details</h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Phone</Label>
            <Input data-testid="profile-phone-input" value={phone ?? profile.phone ?? ""} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Bio (shown on public website)</Label>
            <Textarea data-testid="profile-bio-input" rows={4} value={bio ?? profile.bio ?? ""} onChange={(e) => setBio(e.target.value)} />
          </div>
          <Button onClick={save} data-testid="save-profile-button">Save profile</Button>
        </div>
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-secondary" />Change password</h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Current password</Label>
            <Input data-testid="current-password-input" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">New password (min 8 chars)</Label>
            <Input data-testid="new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <Button variant="outline" onClick={changePw} data-testid="change-password-button">Update password</Button>
        </div>
        <TeacherSelfTeachingPicker />
      </div>
    </div>
  );
}

function TeacherSelfTeachingPicker() {
  const { data: courses } = useList("/courses");
  const { data: teaching, reload } = useList("/teacher/teaching");
  const [courseIds, setCourseIds] = useState(new Set());
  const [levelIds, setLevelIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!teaching) return;
    setCourseIds(new Set(teaching.course_ids || []));
    setLevelIds(new Set(teaching.level_ids || []));
  }, [teaching]);

  const toggleCourse = (cid, allLvlIds) => {
    const c = new Set(courseIds), l = new Set(levelIds);
    if (c.has(cid)) { c.delete(cid); allLvlIds.forEach((id) => l.delete(id)); }
    else { c.add(cid); allLvlIds.forEach((id) => l.add(id)); }
    setCourseIds(c); setLevelIds(l);
  };
  const toggleLevel = (lid, cid) => {
    const c = new Set(courseIds), l = new Set(levelIds);
    if (l.has(lid)) l.delete(lid); else { l.add(lid); c.add(cid); }
    setCourseIds(c); setLevelIds(l);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/teacher/teaching", { course_ids: [...courseIds], level_ids: [...levelIds] });
      toast.success("Teaching scope saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const visibleCourses = useMemo(
    () => (courses || []).filter((c) => c.status !== "inactive"), [courses]);

  // Group by category — same UX as the admin teaching picker.
  const groupedByCategory = useMemo(() => {
    const groups = new Map();
    visibleCourses.forEach((c) => {
      const cat = (c.category || "Other").trim() || "Other";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(c);
    });
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, list]) => [cat, list.sort((a, b) => a.name.localeCompare(b.name))]);
  }, [visibleCourses]);

  return (
    <div className="rounded-lg border bg-card p-6 space-y-3 lg:col-span-2" data-testid="self-teaching-picker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-secondary" />
          Courses &amp; grades I teach
        </h3>
        <Button onClick={save} disabled={busy} data-testid="save-self-teaching">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving..." : "Save"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Tick the courses (and the grades within each) you&apos;re qualified for. Grouped by category. Parents booking online will only see you for these courses.
      </p>
      {!courses ? <Spinner /> : (
        <div className="space-y-5" data-testid="self-teaching-by-category">
          {groupedByCategory.map(([category, list]) => (
            <div key={category} data-testid={`self-teaching-category-${category}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-secondary mb-2">
                {category}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((c) => {
                  const lvls = c.levels || [];
                  const allIds = lvls.map((l) => l.id);
                  const picked = courseIds.has(c.id);
                  return (
                    <div key={c.id} className={`rounded-lg border p-3 ${picked ? "bg-secondary/5 border-secondary/30" : "bg-card"}`}
                         data-testid={`self-teaching-course-${c.id}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={picked} onCheckedChange={() => toggleCourse(c.id, allIds)}
                                  data-testid={`self-teaching-course-toggle-${c.id}`} />
                        <span className="font-medium text-sm">{c.name}</span>
                      </label>
                      {picked && lvls.length > 0 && (
                        <div className="mt-2 ml-6 flex flex-wrap gap-x-3 gap-y-1.5">
                          {lvls.map((l) => (
                            <label key={l.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                              <Checkbox checked={levelIds.has(l.id)}
                                        onCheckedChange={() => toggleLevel(l.id, c.id)}
                                        data-testid={`self-teaching-level-toggle-${l.id}`} />
                              <span>{l.level_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
